import { Outlet, useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Trash2 } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useState } from "react";
import { useCollaboration } from "@/hooks/use-collaboration";
import { useAuthStore } from "@/stores/authStore";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { showSuccess, showError } from "@/utils/toast";

const ScoreLayout = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const { data: project } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => apiClient.getProject(projectId!),
    enabled: !!projectId,
  });

  // Use collaboration hook for permission checks
  const { isOwner } = useCollaboration(
    projectId || "",
    user?._id || ""
  );

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.deleteProject(projectId!),
    onSuccess: () => {
      showSuccess("Project deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      navigate("/dashboard");
    },
    onError: (error: Error) => {
      showError(error.message || "Failed to delete project");
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  const projectTitle = project?.title || "The Last Barista";
  
  return (
    <div className="min-h-screen font-ibm selection:bg-primary selection:text-primary-foreground relative bg-background text-foreground overflow-x-hidden flex flex-col items-center">
      {/* Background Grid Animation (Fixed) */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[size:32px_32px]"></div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent"></div>
      </div>

      <div className="flex-1 flex flex-col items-center w-full min-w-0 relative z-10">
        <nav className="w-full max-w-5xl px-6 py-6 flex items-center justify-between z-30 sticky top-16 md:top-0 bg-background/80 backdrop-blur-md md:backdrop-blur-none md:bg-transparent mt-16 md:mt-0">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="p-2 -ml-2 mr-2 text-muted-foreground hover:text-foreground transition-colors hover:bg-secondary rounded-lg"
              title="Back to Dashboard"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <div className="flex flex-col">
              <div className="flex items-center gap-2 group cursor-pointer">
                <h2 className="font-cabinet font-semibold text-foreground text-lg tracking-tight">
                  {projectTitle}
                </h2>
              </div>
              <span className="text-xs text-muted-foreground font-ibm">
                Last edited 2h ago
              </span>
            </div>
          </div>
          {isOwner && (
            <button
              onClick={() => setShowDeleteDialog(true)}
              className="p-2.5 rounded-xl hover:bg-red-50 text-foreground hover:text-red-600 transition-colors border border-transparent hover:border-red-100"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </nav>

        <main className="flex-1 flex flex-col w-full max-w-5xl pt-8 pr-6 pb-20 pl-6 items-center">
          <Outlet />
        </main>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{projectTitle}"? This action cannot be undone and will permanently delete all associated data including scripts and scores.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ScoreLayout;